import { AppPermission } from "@closerplatform/spinner-openapi";
import { L10nId } from "@fidget/common/i18n";

export interface PermissionField {
  textId: L10nId;
  permissions: AppPermission[];
  /**
   * Antagonistic permissions get unselected when this permission gets selected.
   * This permission will not be displayed as selected when all of antagonistic permisisons are selected
   * */
  antagonisticPermissions?: AppPermission[];
}

interface PermissionGroup {
  textId: L10nId;
  permissionField: PermissionField;
}

const profilePermissions: PermissionGroup = {
  textId: "settings.rolesSettings.permissions.nameAvatar",
  permissionField: {
    textId: "settings.rolesSettings.functionality.editPersonal",
    permissions: [AppPermission.UpdatePersonalData]
  }
};

const allConversationsPermissions = [
  AppPermission.DisplayClosedConversations,
  AppPermission.DisplayWaitingConversations,
  AppPermission.DisplayInProgressConversations,
  AppPermission.DisplayFollowedConversations,
  AppPermission.DisplayYoursConversations
];

const conversationsYours: PermissionGroup = {
  textId: "settings.rolesSettings.permissions.conversationsYours",
  permissionField: {
    textId: "settings.rolesSettings.functionality.conversationsYours",
    permissions: [AppPermission.DisplayYoursConversations],
    antagonisticPermissions: allConversationsPermissions.filter(
      (el) => el !== AppPermission.DisplayYoursConversations
    )
  }
};

const conversationsAll: PermissionGroup = {
  textId: "settings.rolesSettings.permissions.conversationsAll",
  permissionField: {
    textId: "settings.rolesSettings.functionality.conversationsAll",
    permissions: allConversationsPermissions
  }
};

const conversationsTagGroups: PermissionGroup = {
  textId: "settings.rolesSettings.permissions.conversationsTagGroups",
  permissionField: {
    textId: "settings.rolesSettings.functionality.conversationsTagGroups",
    permissions: [AppPermission.DisplayOwnTagGroupsConversations]
  }
};

const historicallyAssignedConversations: PermissionGroup = {
  textId: "settings.rolesSettings.permissions.historicallyAssignedConversations",
  permissionField: {
    textId: "settings.rolesSettings.functionality.historicallyAssignedConversations",
    permissions: [AppPermission.DisplayHistoricallyAssignedConversations]
  }
};

const reportsYoursEfficiency: PermissionGroup = {
  textId: "settings.rolesSettings.permissions.reportsYoursEfficiency",
  permissionField: {
    textId: "settings.rolesSettings.functionality.reportsYoursEfficiency",
    permissions: [AppPermission.ReportsYoursEfficiency],
  }
};

const reportsGroupsEfficiency: PermissionGroup = {
  textId: "settings.rolesSettings.permissions.reportsGroupsEfficiency",
  permissionField: {
    textId: "settings.rolesSettings.functionality.reportsGroupsEfficiency",
    permissions: [AppPermission.ReportsGroupsEfficiency],
  }
};

const reportsConversationsServiceLevelTagGroups: PermissionGroup = {
  textId: "settings.rolesSettings.permissions.reportsConversationsServiceLevelTagGroups",
  permissionField: {
    textId: "settings.rolesSettings.functionality.reportsConversationsServiceLevelTagGroups",
    permissions: [AppPermission.ReportsConversationsServiceLevelTagGroups],
  }
};

const reportsConversationsServiceLevelTags: PermissionGroup = {
  textId: "settings.rolesSettings.permissions.reportsConversationsServiceLevelTags",
  permissionField: {
    textId: "settings.rolesSettings.functionality.reportsConversationsServiceLevelTags",
    permissions: [AppPermission.ReportsConversationsServiceLevelTags],
  }
};

const reportsConversationsBackLineTags: PermissionGroup = {
  textId: "settings.rolesSettings.permissions.reportsConversationsBackLineTags",
  permissionField: {
    textId: "settings.rolesSettings.functionality.reportsConversationsBackLineTags",
    permissions: [AppPermission.ReportsConversationsBackLineTags],
  }
};

const reportsAdvisersAdviserGroups: PermissionGroup = {
  textId: "settings.rolesSettings.permissions.reportsAdvisersAdviserGroups",
  permissionField: {
    textId: "settings.rolesSettings.functionality.reportsAdvisersAdviserGroups",
    permissions: [AppPermission.ReportsAdvisersAdviserGroups],
  }
};

const reportsAdvisersTagGroups: PermissionGroup = {
  textId: "settings.rolesSettings.permissions.reportsAdvisersTagGroups",
  permissionField: {
    textId: "settings.rolesSettings.functionality.reportsAdvisersTagGroups",
    permissions: [AppPermission.ReportsAdvisersTagGroups],
  }
};

const reportsOrgOther: PermissionGroup = {
  textId: "settings.rolesSettings.permissions.reportsOrgOther",
  permissionField: {
    textId: "settings.rolesSettings.functionality.reportsOrgOther",
    permissions: [AppPermission.ReportsOrgOther],
  }
};

const reportsOrgEfficiency: PermissionGroup = {
  textId: "settings.rolesSettings.permissions.reportsOrgEfficiency",
  permissionField: {
    textId: "settings.rolesSettings.functionality.reportsOrgEfficiency",
    permissions: [AppPermission.ReportsOrgEfficiency],
  }
};

const proactiveMessages: PermissionGroup = {
  textId: "settings.rolesSettings.permissions.proactiveMessages",
  permissionField: {
    textId: "settings.rolesSettings.functionality.proactiveMessages",
    permissions: [AppPermission.SettingsProactiveMessages]
  }
};

const tags: PermissionGroup = {
  textId: "settings.rolesSettings.permissions.tags",
  permissionField: {
    textId: "settings.rolesSettings.functionality.tags",
    permissions: [AppPermission.SettingsTags]
  }
};

const aiSuggestions: PermissionGroup = {
  textId: "settings.rolesSettings.permissions.ai",
  permissionField: {
    textId: "settings.rolesSettings.functionality.ai",
    permissions: [AppPermission.SettingsAiSuggestions]
  }
};

const leadDeleteProfile: PermissionGroup = {
  textId: "settings.rolesSettings.permissions.leadDeleteProfile",
  permissionField: {
    textId: "settings.rolesSettings.functionality.leadDeleteProfile",
    permissions: [AppPermission.LeadDeleteProfile]
  }
};

const webhooks: PermissionGroup = {
  textId: "settings.rolesSettings.permissions.webhooks",
  permissionField: {
    textId: "settings.rolesSettings.functionality.webhooks",
    permissions: [AppPermission.SettingsWebhooks]
  }
};

const advisersConfiguration: PermissionGroup = {
  textId: "settings.rolesSettings.permissions.advisers.configuration",
  permissionField: {
    textId: "settings.rolesSettings.functionality.advisers.configuration",
    permissions: [AppPermission.SettingsAdvisersConfiguration]
  }
};

const advisersAssignments: PermissionGroup = {
  textId: "settings.rolesSettings.permissions.advisers.assignments",
  permissionField: {
    textId: "settings.rolesSettings.functionality.advisers.assignments",
    permissions: [AppPermission.SettingsAdvisersAssignments]
  }
};

const advisersCapacity: PermissionGroup = {
  textId: "settings.rolesSettings.permissions.advisers.autoAssignedLimit",
  permissionField: {
    textId: "settings.rolesSettings.functionality.advisers.autoAssignedLimit",
    permissions: [AppPermission.SettingsAdvisersAutoAssignedLimit]
  }
};

const advisersAvatar: PermissionGroup = {
  textId: "settings.rolesSettings.permissions.advisers.avatar",
  permissionField: {
    textId: "settings.rolesSettings.functionality.advisers.avatar",
    permissions: [AppPermission.SettingsAdvisersAvatar]
  }
};

const agentGroupsConfiguration: PermissionGroup = {
  textId: "settings.rolesSettings.permissions.agentGroups.configuration",
  permissionField: {
    textId: "settings.rolesSettings.functionality.agentGroups.configuration",
    permissions: [AppPermission.SettingsAgentGroupsConfiguration]
  }
};

const forms: PermissionGroup = {
  textId: "settings.rolesSettings.permissions.forms",
  permissionField: {
    textId: "settings.rolesSettings.functionality.forms",
    permissions: [AppPermission.SettingsActionForms]
  }
};

const advisersStatus: PermissionGroup = {
  textId: "settings.rolesSettings.permissions.advisersStatus",
  permissionField: {
    textId: "settings.rolesSettings.functionality.advisersStatus",
    permissions: [AppPermission.SettingsStatuses]
  }
};

const advisersRole: PermissionGroup = {
  textId: "settings.rolesSettings.permissions.role",
  permissionField: {
    textId: "settings.rolesSettings.functionality.role",
    permissions: [AppPermission.SettingsRoles]
  }
};

const settingsGeneral: PermissionGroup = {
  textId: "settings.rolesSettings.permissions.settingsGeneral",
  permissionField: {
    textId: "settings.rolesSettings.functionality.settingsGeneral",
    permissions: [AppPermission.SettingsGeneral]
  }
};

const selfAssign: PermissionGroup = {
  textId: "settings.rolesSettings.permissions.selfAssign",
  permissionField: {
    textId: "settings.rolesSettings.functionality.selfAssign",
    permissions: [AppPermission.RoomAssignSelf]
  }
};

const assignToAdviser: PermissionGroup = {
  textId: "settings.rolesSettings.permissions.assignToAdviser",
  permissionField: {
    textId: "settings.rolesSettings.functionality.assignToAdviser",
    permissions: [AppPermission.RoomAssignAgent]
  }
};

const assignToTag: PermissionGroup = {
  textId: "settings.rolesSettings.permissions.assignToTag",
  permissionField: {
    textId: "settings.rolesSettings.functionality.assignToTag",
    permissions: [AppPermission.RoomAssignByTag]
  }
};

const assignToTagGroup: PermissionGroup = {
  textId: "settings.rolesSettings.permissions.assignToTagGroup",
  permissionField: {
    textId: "settings.rolesSettings.functionality.assignToTagGroup",
    permissions: [AppPermission.RoomAssignByTagGroup]
  }
};

const leadUpdateProfile: PermissionGroup = {
  textId: "settings.rolesSettings.permissions.leadUpdateProfile",
  permissionField: {
    textId: "settings.rolesSettings.functionality.leadUpdateProfile",
    permissions: [AppPermission.LeadUpdateProfile]
  }
};

const aiIntentsClassify: PermissionGroup = {
  textId: "settings.rolesSettings.permissions.aiIntentsClassify",
  permissionField: {
    textId: "settings.rolesSettings.functionality.aiIntentsClassify",
    permissions: [AppPermission.AiIntentsClassify]
  }
};

const settingsPersonal: PermissionGroup = {
  textId: "settings.rolesSettings.permissions.settingsPersonal",
  permissionField: {
    textId: "settings.rolesSettings.functionality.settingsPersonal",
    permissions: [AppPermission.SettingsPersonal]
  }
};

const settingsPasswordChange: PermissionGroup = {
  textId: "settings.rolesSettings.permissions.settingsPasswordChange",
  permissionField: {
    textId: "settings.rolesSettings.functionality.settingsPasswordChange",
    permissions: [AppPermission.SettingsPasswordChange]
  }
};

const meetingsPageVisibility: PermissionGroup = {
  textId: "settings.rolesSettings.permissions.meetingsPageVisibility",
  permissionField: {
    textId: "settings.rolesSettings.functionality.meetingsPageVisibility",
    permissions: [AppPermission.MeetingsPageVisibility]
  }
};

const helpPageVisibility: PermissionGroup = {
  textId: "settings.rolesSettings.permissions.helpPageVisibility",
  permissionField: {
    textId: "settings.rolesSettings.functionality.helpPageVisibility",
    permissions: [AppPermission.HelpPageVisibility]
  }
};

export const permissionGroupsList: PermissionGroup[] = [
  profilePermissions,
  conversationsYours,
  conversationsAll,
  conversationsTagGroups,
  historicallyAssignedConversations,
  reportsYoursEfficiency,
  reportsGroupsEfficiency,
  reportsConversationsServiceLevelTagGroups,
  reportsConversationsServiceLevelTags,
  reportsConversationsBackLineTags,
  reportsAdvisersAdviserGroups,
  reportsAdvisersTagGroups,
  reportsOrgOther,
  reportsOrgEfficiency,
  proactiveMessages,
  tags,
  aiSuggestions,
  leadDeleteProfile,
  webhooks,
  advisersConfiguration,
  agentGroupsConfiguration,
  advisersAssignments,
  advisersCapacity,
  advisersAvatar,
  forms,
  advisersStatus,
  advisersRole,
  settingsGeneral,
  selfAssign,
  assignToAdviser,
  assignToTag,
  assignToTagGroup,
  leadUpdateProfile,
  aiIntentsClassify,
  settingsPasswordChange,
  settingsPersonal,
  meetingsPageVisibility,
  helpPageVisibility
];
